import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import "./dashboard.css";
import Header from "../../components/header";
import CalendarIcon from "./assets/calendarIcon.png";
import DocumentIcon from "./assets/documentIcon.png";
import OnlineIcon from "./assets/onlineIcon.png";
import { Skeleton } from "@mui/material";
import InfoIcon from "./assets/infoIcon.png";
import DownIcon from "../../home/assets/downIcon.png";
import WarningIcon from "./assets/warningIcon.png";
import ReviewIcon from "./assets/reviewIcon.png";
import GraphPlaceholder from "./assets/graphPlaceholder.png";
import ButtonGroup from "../../../components/buttonGroup";
import BreakdownPlaceholder from "./assets/breakdownPlaceholder.png";
import UserPlaceholder from "./assets/userPlaceholder.png";
import PaymentIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import { LineChart, BarChart, PieChart } from '@mui/x-charts';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { authenticatedFetch } from "../../../network/api";


const PRICE_PER_APPT_FALLBACK = 110; // move to env/config
const SOFT_ALERT_RATIO = 0.8; // 80%

function Dashboard() {
  const [selectedButton, setSelectedButton] = useState("Appointment reason");
  const [stripeStats, setStripeStats] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState(null);

  // Add state for stats API
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Get firmId from Redux
  const firmId = useSelector(state => state.attorneyData.attorneyData?.firm_id);
  const attorneyData = useSelector(state => state.attorneyData.attorneyData);
  const isAdmin = attorneyData?.role === 'firm_admin' || attorneyData?.admin === true;

  // Manage Spend modal state
  const [manageSpendOpen, setManageSpendOpen] = useState(false);
  const [subModal, setSubModal] = useState(null); // 'budget' | 'lawyers' | 'pause' | null
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    async function fetchStripeStats() {
      setStripeLoading(true);
      setStripeError(null);
      try {
        const res = await authenticatedFetch(`/api/attorney/webapp/stripe/firmStats?firm_id=${firmId}`);
        if (!res.ok) throw new Error("Failed to fetch Stripe stats");
        const data = await res.json();
        setStripeStats(data);
      } catch (err) {
        setStripeError(err.message);
        setStripeStats(null);
      } finally {
        setStripeLoading(false);
      }
    }
    if (firmId) fetchStripeStats();
  }, [firmId]);

  // Fetch stats API on load
  useEffect(() => {
    async function fetchStats() {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const res = await fetch(`/api/attorney/webapp/dashboard/stats?firm_id=${firmId}`);
        if (!res.ok) throw new Error("Failed to fetch dashboard stats");
        const data = await res.json();
        setStats(data);
      } catch (err) {
        setStatsError(err.message);
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    }
    if (firmId) fetchStats();
  }, [firmId]);

  const handleButtonSelect = (buttonName) => {
    setSelectedButton(buttonName);
  };

  // --- Data Transformations ---
  // 1. Bookings Over Time (by date, by lawyer)
  const bookingsOverTime = React.useMemo(() => {
    if (!stats || !stats.appointments) return [];
    const dateLawyerMap = {};
    stats.appointments.forEach(appt => {
      const date = appt.requested_slot?.date;
      const lawyer = appt.lawyerName || 'Unknown';
      if (!date) return;
      if (!dateLawyerMap[date]) dateLawyerMap[date] = { date };
      dateLawyerMap[date][lawyer] = (dateLawyerMap[date][lawyer] || 0) + 1;
      dateLawyerMap[date].total = (dateLawyerMap[date].total || 0) + 1;
    });
    // Sort by date ascending
    return Object.values(dateLawyerMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [stats]);
  const lawyerNames = React.useMemo(() => {
    if (!stats || !stats.appointments) return [];
    const set = new Set(stats.appointments.map(a => a.lawyerName || 'Unknown'));
    return Array.from(set);
  }, [stats]);

  // 2. New Client Value
  const newClientValue = React.useMemo(() => {
    if (!stats || !stats.appointments) return 0;
    const clientTotals = {};
    stats.appointments.forEach(appt => {
      const client = appt.client_id;
      const price = (appt.appointmentType?.appointment_price || 0) / 100; // convert cents to dollars
      clientTotals[client] = (clientTotals[client] || 0) + price;
    });
    const total = Object.values(clientTotals).reduce((a, b) => a + b, 0);
    const numClients = Object.keys(clientTotals).length;
    const value = numClients ? Math.round(total / numClients) : 0;

    return value;
  }, [stats]);

  // 3. Appointment Reason Breakdown
  const appointmentReasonData = React.useMemo(() => {
    if (!stats || !stats.appointments) return [];
    const reasonMap = {};
    stats.appointments.forEach(appt => {
      const reason = appt.appointmentType?.appointment_name || 'Unknown';
      reasonMap[reason] = (reasonMap[reason] || 0) + 1;
    });
    return Object.entries(reasonMap).map(([name, value]) => ({ name, value }));
  }, [stats]);

  // 4. Booking Type Breakdown
  const bookingTypeData = React.useMemo(() => {
    if (!stats || !stats.appointments) return [];
    const typeMap = {};
    stats.appointments.forEach(appt => {
      const type = appt.appointmentType?.appointment_type || 'Unknown';
      typeMap[type] = (typeMap[type] || 0) + 1;
    });
    return Object.entries(typeMap).map(([name, value]) => ({ name, value }));
  }, [stats]);

  // 5. Recent Bookings Table
  const recentBookings = React.useMemo(() => {
    if (!stats || !stats.appointments) return [];
    return [...stats.appointments]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [stats]);

  // --- Chart Colors ---
  const chartColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  const getColorForLawyer = (name) => {
    const idx = lawyerNames.indexOf(name);
    return chartColors[idx % chartColors.length];
  };

  return (
    <div style={{ fontFamily: 'Poppins' }}>
      <Header />
     
      <div className="dashboard-container-outer">


        {/* Main Manage Spend Modal */}
        <Dialog
          open={manageSpendOpen}
          onClose={() => setManageSpendOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '20px',
              minWidth: 400,
              maxWidth: 700,
              fontFamily: 'Poppins',
              padding: 0,
            },
          }}
        >
          <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 26, color: '#144a8c', pb: 0 }}>
            Choose how to manage your spending
            <IconButton
              aria-label="close"
              onClick={() => setManageSpendOpen(false)}
              sx={{ position: 'absolute', right: 16, top: 16, color: '#144a8c' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ fontFamily: 'Poppins', pt: 1, pb: 2 }}>
            <Typography sx={{ color: '#444', mb: 3, fontSize: 16 }}>
              Control your firm's monthly spend, lawyer visibility, and booking status.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: '16px',
                  borderColor: '#144a8c',
                  color: '#144a8c',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  padding: '18px 20px',
                  fontSize: '1.1rem',
                  background: '#f7fafd',
                  textTransform: 'none',
                  '&:hover': { background: '#e3eefc', borderColor: '#144a8c' },
                }}
                onClick={() => { setSubModal('budget'); setManageSpendOpen(false); }}
              >
                <Box>
                  <p style={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 16 }}>Set a monthly spend cap</p>
                  <p style={{ fontWeight: 400, color: '#555', fontSize: 14, fontFamily: 'Poppins' }}>
                    Once you reach your spend cap, all lawyers become unbookable until it resets.
                  </p>
                </Box>
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: '16px',
                  borderColor: '#144a8c',
                  color: '#144a8c',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  padding: '18px 20px',
                  fontSize: '1.1rem',
                  background: '#f7fafd',
                  textTransform: 'none',
                  '&:hover': { background: '#e3eefc', borderColor: '#144a8c' },
                }}
                onClick={() => { setSubModal('lawyers'); setManageSpendOpen(false); }}
              >
                <Box>
                <p style={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 16 }}>Manage which lawyers can appear in search</p>
                <p style={{ fontWeight: 400, color: '#555', fontSize: 14, fontFamily: 'Poppins' }}>
                    Select which lawyers are open to new client bookings.
                  </p>
                </Box>
              </Button>
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: '16px',
                  borderColor: '#144a8c',
                  color: '#144a8c',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  padding: '18px 20px',
                  fontSize: '1.1rem',
                  background: '#f7fafd',
                  textTransform: 'none',
                  '&:hover': { background: '#e3eefc', borderColor: '#144a8c' },
                }}
                onClick={() => { setSubModal('pause'); setManageSpendOpen(false); }}
              >
                <Box>
                <p style={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 16 }}>Pause spending and block new bookings</p>
                <p style={{ fontWeight: 400, color: '#555', fontSize: 14, fontFamily: 'Poppins' }}>
                    New clients will not be able to book with anyone in your firm.
                  </p>
                </Box>
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />

        {/* Sub-modals for each action (to be implemented next) */}
        {/* TODO: Implement sub-modals for 'budget', 'lawyers', 'pause' */}

        {/* 1. Top "3 steps" section */}

        <div className="steps-row-outer">
          <h2 className="steps-row-title">
            Reach more clients in just three steps
          </h2>

          <div className="steps-row">
            <div className="step-card">
              <img
                src={CalendarIcon}
                style={{ width: "42px", height: "42px" }}
              />

              <h3>Manage QuidPro from any device</h3>
              <p>
                Save time on manual calendar tasks by integrating your Quidpro
                and practice calendars.{" "}
              </p>
              <div className="button-row">
                <button className="decline-button">Not right now</button>
                <button className="accept-button">
                  Integrate your calendar
                </button>
              </div>
            </div>
            <div className="step-card">
              <img
                src={DocumentIcon}
                style={{ width: "42px", height: "42px" }}
              />
              <h3>Automated Document Collection</h3>
              <p>
                Save time by eliminating the need to chase down documents from
                clients.
              </p>
              <div className="button-row">
                <button className="decline-button">Not right now</button>
                <button className="accept-button">Learn more</button>
              </div>{" "}
            </div>
            <div className="step-card">
              <img src={OnlineIcon} style={{ width: "42px", height: "42px" }} />

              <h3>Enable Online Consultation</h3>
              <p>
                Let clients schedule consultations directly from your website.
              </p>
              <div className="button-row">
                <button className="decline-button">Not right now</button>
                <button className="accept-button">Learn more</button>
              </div>
            </div>
          </div>
        </div>



        {/* --- Stripe Firm Financials Section (custom layout) --- */}
        <div className="stripe-financials-section">
          <div className="stripe-financials-header">
            <h2>Firm Financials (Stripe)</h2>
            <span style={{ color: '#888', fontSize: 14, marginLeft: 8 }}>
              {stripeLoading ? "Loading..." : stripeError ? stripeError : ""}
            </span>
                            {/* Manage Spend Button (admin only) */}
                            {isAdmin && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" >
            <Button
              variant="contained"
              onClick={() => setManageSpendOpen(true)}
              sx={{
                backgroundColor: '#144a8c',
                borderRadius: '10px',
                fontFamily: 'Poppins',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(20,74,140,0.08)',
                '&:hover': { backgroundColor: '#10386a' },
                padding: '10px 28px',
                fontSize: '1rem',
              }}
            >
              Manage Spend
            </Button>
          </Box>
        )}
          </div>
          {!firmId ? (
            <div style={{ padding: 24, color: '#c00', fontWeight: 500 }}>
              No firm ID found. Please check your account setup.
            </div>
          ) : (
            <div className="stripe-financials-cards-row">
              <div className="stripe-financials-card">
                <div className="stripe-financials-icon"><PaymentIcon fontSize="large" /></div>
                <div>
                  <div className="stripe-financials-label">Total Revenue</div>
                  {stripeLoading ? (
                    <Skeleton variant="text" width={80} />
                  ) : stripeStats && stripeStats.totalRevenue !== undefined ? (
                    <div className="stripe-financials-value">${stripeStats.totalRevenue.toLocaleString()}</div>
                  ) : (
                    <div className="stripe-financials-value">--</div>
                  )}
                </div>
              </div>
              <div className="stripe-financials-card">
                <div className="stripe-financials-icon"><TrendingUpIcon fontSize="large" /></div>
                <div>
                  <div className="stripe-financials-label">This Month</div>
                  {stripeLoading ? (
                    <Skeleton variant="text" width={80} />
                  ) : stripeStats && stripeStats.monthRevenue !== undefined ? (
                    <div className="stripe-financials-value">${stripeStats.monthRevenue.toLocaleString()}</div>
                  ) : (
                    <div className="stripe-financials-value">--</div>
                  )}
                </div>
              </div>
              <div className="stripe-financials-card">
                <div className="stripe-financials-icon"><AccountBalanceWalletIcon fontSize="large" /></div>
                <div>
                  <div className="stripe-financials-label">Outstanding Balance</div>
                  {stripeLoading ? (
                    <Skeleton variant="text" width={80} />
                  ) : stripeStats && stripeStats.outstanding !== undefined ? (
                    <div className="stripe-financials-value">${stripeStats.outstanding.toLocaleString()}</div>
                  ) : (
                    <div className="stripe-financials-value">--</div>
                  )}
                </div>
              </div>
              <div className="stripe-financials-card">
                <div className="stripe-financials-icon"><PaidIcon fontSize="large" /></div>
                <div>
                  <div className="stripe-financials-label">Next Payout</div>
                  {stripeLoading ? (
                    <Skeleton variant="text" width={120} />
                  ) : stripeStats && stripeStats.nextPayoutAmount ? (
                    <div className="stripe-financials-value">${stripeStats.nextPayoutAmount} on {stripeStats.nextPayoutDate}</div>
                  ) : (
                    <div className="stripe-financials-value">No payout scheduled</div>
                  )}
                </div>
              </div>
            </div>
          )}

{isAdmin && <BudgetManagementSection firmId={firmId} />}
          {/* Fancy tables for recent transactions and payouts */}
          {firmId && (
            <div className="stripe-financials-tables-row">
              <div className="stripe-financials-table-card">
                <div className="stripe-financials-table-title"><ReceiptLongIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />Recent Transactions</div>
                {stripeLoading ? (
                  <Skeleton variant="rectangular" width={320} height={40} />
                ) : stripeStats && stripeStats.recentTransactions && stripeStats.recentTransactions.length > 0 ? (
                  <table className="stripe-financials-table">
                    <thead>
                      <tr>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stripeStats.recentTransactions.map((tx) => (
                        <tr key={tx.id}>
                          <td>${tx.amount}</td>
                          <td>{tx.status}</td>
                          <td>{tx.created.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="stripe-financials-table-placeholder">No recent transactions</div>
                )}
              </div>
              <div className="stripe-financials-table-card">
                <div className="stripe-financials-table-title"><PaidIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />Recent Payouts</div>
                {stripeLoading ? (
                  <Skeleton variant="rectangular" width={220} height={40} />
                ) : stripeStats && stripeStats.recentPayouts && stripeStats.recentPayouts.length > 0 ? (
                  <table className="stripe-financials-table">
                    <thead>
                      <tr>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stripeStats.recentPayouts.map((p) => (
                        <tr key={p.id}>
                          <td>${p.amount}</td>
                          <td>{p.status}</td>
                          <td>{p.arrival_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="stripe-financials-table-placeholder">No recent payouts</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="performance-section">
          <div className="performance-header">
            <div className="performance-title">
              <h2>Performance since last statement</h2>
              <p>(2/26 – 3/7/25)</p>
            </div>
            <div className="performance-actions">
              <button className="last-statement">
                See last statement
                <img src={DownIcon} style={{ width: "24px", height: "24px" }} />
              </button>
              <button className="learn-more">
                Learn more
                <img src={DownIcon} style={{ width: "24px", height: "24px" }} />
              </button>
            </div>
          </div>

          {/* 3. Top stats row */}
          <div className="stats-row">
            <div className="bookings-card">
              <div className="bookings-card-header">
                <h4 className="bookings-card-title">{statsLoading ? <Skeleton width={60} /> : stats ? `${stats.totalLifetimeBookings} total bookings` : '0 total bookings'}</h4>
                <p className="bookings-link">View details</p>
              </div>
              <div className="bookings-content">
                <div className="bookings-content-left">
                  <p className="bookings-card-subtitle">By client type</p>
                  {/* Placeholder, as client type is not in data */}
                  <Skeleton variant="text" width={"300px"} />
                </div>

                <div
                  style={{
                    height: "80%",
                    width: "1px",
                    backgroundColor: "#D9D9D9",
                  }}
                />

                <div className="bookings-content-right">
                  <p className="bookings-card-subtitle">
                    Turn Google searches into free bookings
                  </p>

                  <p className="bookings-card-body">
                    Get up to 78% more client bookings directly
                  </p>
                </div>
              </div>
            </div>

            <div className="search-score-card">
              <div className="score-card-header">
                <h4 className="score-card-title">QuidPro Search Score</h4>
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </div>
              <div className="score-card-content">
                <p className="score-card-placeholder">You currently have no attorneys</p>
                <p className="score-link">View attorneys</p>
              </div>
            </div>
          </div>
        </div>

        <div className="review-section">
          <div className="stats-row">
            <div className="spend-section">
              <h2 className="bookings-card-title">
                {" "}
                Your spend
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </h2>

              <div className="spend-card">
                <div className="spend-card-content">
                  <p className="spend-link">
                    View invoice details for this time period
                  </p>
                  <div className="disclaimer-box">
                    <img
                      src={WarningIcon}
                      style={{ width: "20px", height: "20px" }}
                    />
                    <p className="disclaimer-text">
                      New clients cannot book with your firm on Quidpro because
                      none of your attorneys are open to new clients.
                      <p className="disclaimer-link">Manage your attorneys</p>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="review-box">
              <h2 className="bookings-card-title">
                {" "}
                Ratings & review
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </h2>

              <div className="bookings-card">
                <div className="review-card-content">
                  <div className="bookings-content-left">
                    <img
                      src={ReviewIcon}
                      style={{ width: "48px", height: "48px" }}
                    />

                    <p className="review-placeholder">No reviews yet</p>
                  </div>

                  <div
                    style={{
                      height: "80%",
                      width: "1px",
                      backgroundColor: "#D9D9D9",
                    }}
                  />

                  <div className="review-content-right">
                    <p className="bookings-card-subtitle">
                      Kickstart your profile with your first review
                    </p>

                    <p className="bookings-card-body">
                      Get reviews from at least 10 clients to stand out in
                      search and receive more bookings.
                    </p>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                      }}
                    >
                      <Skeleton variant="text" width={"300px"} />
                      <p className="progress-text">0 of 10</p>
                    </div>

                    <p className="spend-link">Request client reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Bookings Over Time --- */}
        <div className="value-section">
          <div className="stats-row">
            <div className="time-box fancy-card fancy-chart-container">
              <h2 className="bookings-card-title">
                Bookings over time
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </h2>
              <div style={{ width: '100%' }}>
                {statsLoading ? (
                  <Skeleton variant="rectangular" width={400} height={200} />
                ) : bookingsOverTime.length === 0 ? (
                  <p className="review-placeholder">Your bookings over time will appear here.</p>
                ) : (
                  <LineChart
                    dataset={bookingsOverTime}
                    xAxis={[{ scaleType: 'point', dataKey: 'date' }]}
                    series={[
                      ...lawyerNames.map(name => ({
                        dataKey: name,
                        label: name,
                        color: getColorForLawyer(name),
                        showMark: true,
                      })),
                      {
                        dataKey: 'total',
                        label: 'Total',
                        color: '#222',
                        showMark: true,
                      },
                    ]}
                    height={250}
                    style={{ width: '100%' }}
                  />
                )}
              </div>
            </div>
            {/* --- New Client Value --- */}
            <div className="spend-section fancy-card" style={{ flex: '0 1 calc(30% - 15px)' }}>
              <h2 className="bookings-card-title">
                New client value
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </h2>
              <div className="value-card-header">
                <h2 className="value-card-title">{statsLoading ? <Skeleton width={60} /> : `$${newClientValue.toLocaleString()}`}</h2>
              </div>
              <div className="value-card-content">
                <div className="value-card-left">
                  <h2 className="value-card-body">Average you receive per new client in their first year</h2>
                </div>
                <div className="value-card-right">
                  <img src={WarningIcon} style={{ width: "20px", height: "20px" }} />
                  <p className="score-link">Update</p>
                  <p className="bookings-card-subtitle">${newClientValue.toLocaleString()}</p>
                </div>
              </div>
              <div className="value-card-content">
                <div className="value-card-left">
                  <h2 className="value-card-body">New client bookings from Quidpro</h2>
                </div>
                <div className="value-card-right">
                  <p className="bookings-card-subtitle">{statsLoading ? <Skeleton width={30} /> : stats ? Object.keys(stats.appointments.reduce((acc, a) => { acc[a.client_id] = true; return acc; }, {})).length : 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Bookings Breakdown & Recent Bookings Row --- */}
        <div className="last-section">
          <div className="stats-row">
            <div className="breakdown-box fancy-card fancy-chart-container" style={{ flex: '0 1 calc(45% - 15px)' }}>
              <h2 className="bookings-card-title">
                Bookings breakdown
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </h2>
              {/* <div className="breakdown-card fancy-chart-container"> */}
                <div className="time-card-content">
                  <ButtonGroup buttonNames={["Appointment reason", "Booking type"]} onButtonSelect={handleButtonSelect} />
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 20, marginBottom: 30 }}>
                    {selectedButton === "Appointment reason" ? (
                      statsLoading ? <Skeleton variant="rectangular" width={300} height={150} /> : appointmentReasonData.length === 0 ? (
                        <p className="review-placeholder">Your visit reasons will appear here</p>
                      ) : (
                        <BarChart
                          dataset={appointmentReasonData}
                          xAxis={[{ scaleType: 'linear', dataKey: 'value' }]}
                          yAxis={[{ scaleType: 'band', dataKey: 'name' }]}
                          series={[{ dataKey: 'value', color: '#8884d8' }]}
                          layout="horizontal"
                          height={250}
                          style={{ width: '100%' }}
                          margin={{ left: 200, right: 20, top: 20, bottom: 20 }}
                        />
                      )
                    ) : (
                      statsLoading ? <Skeleton variant="rectangular" width={300} height={150} /> : bookingTypeData.length === 0 ? (
                        <p className="review-placeholder">Your booking types will appear here</p>
                      ) : (
                        <PieChart
                          series={[
                            {
                              data: bookingTypeData,
                              innerRadius: 30,
                              outerRadius: 70,
                              paddingAngle: 2,
                              cornerRadius: 4,
                              startAngle: -90,
                              endAngle: 270,
                              cx: 150,
                              cy: 90,
                            },
                          ]}
                          width={350}
                          height={180}
                        />
                      )
                    )}
                  </div>
                {/* </div> */}
              </div>
            </div>
            {/* --- Recent Booking Details --- */}
            <div className="spend-section fancy-card" style={{ flex: '0 1 calc(55% - 15px)' }}>
              <h2 className="bookings-card-title">
                Recent booking details
                <img src={InfoIcon} style={{ width: "24px", height: "24px" }} />
              </h2>
              <div className="details-card fancy-table-container" style={{  maxHeight: 260 }}>
                {statsLoading ? <Skeleton variant="rectangular" width={320} height={40} /> : recentBookings.length === 0 ? (
                  <>
                    <p className="review-placeholder">Your most recent bookings will appear here</p>
                    <img src={UserPlaceholder} style={{ width: "120px", height: "120px" }} />
                  </>
                ) : (
                  <table className="fancy-table recent-bookings-table">
                    <thead>
                      <tr>
                        <th>Lawyer</th>
                        <th>Reason</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Client</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentBookings.slice(0, 6).map((b, idx) => (
                        <tr key={b.id} className={idx % 2 === 0 ? 'even-row' : 'odd-row'}>
                          <td>{b.lawyerName}</td>
                          <td>{b.appointmentType?.appointment_name}</td>
                          <td>{b.requested_slot?.date}</td>
                          <td>{b.requested_slot?.time}</td>
                          <td>{b.clientName}</td>
                          <td>{b.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            width: "100%",
            textAlign: "left",
            padding: "2%",
          }}
        >
          <p className="help-text">Need help?</p>
        </div>
      </div>

      {subModal === 'budget' && (
        <BudgetModal open={true} onClose={() => setSubModal(null)} firmId={firmId} setSnackbar={setSnackbar} />
      )}
      {subModal === 'lawyers' && (
        <LawyersModal open={true} onClose={() => setSubModal(null)} firmId={firmId} setSnackbar={setSnackbar} />
      )}
      {subModal === 'pause' && (
        <PauseModal open={true} onClose={() => setSubModal(null)} firmId={firmId} setSnackbar={setSnackbar} />
      )}
    </div>
  );
}


function BudgetManagementSection({ firmId }) {
  const [firm, setFirm] = useState(null);            // { monthly_budget, currency, timezone, price_per_appt }
  const [lawyers, setLawyers] = useState([]);
  const [appointments, setAppointments] = useState([]); // raw or monthly-only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);
  const [editBudget, setEditBudget] = useState("");

  // TODO: replace with React Query/SWR for caching/retries
  useEffect(() => {
    async function fetchData() {
      if (!firmId) return;
      setLoading(true);
      setError("");
      try {
        const [lawyersRes, firmRes, statsRes] = await Promise.all([
          authenticatedFetch(`/api/attorney/fetchFirmMembers?firm_id=${firmId}`),
          authenticatedFetch(`/api/attorney/webapp/getFirmBudget?firm_id=${firmId}`),
          authenticatedFetch(`/api/attorney/webapp/dashboard/stats?firm_id=${firmId}`), // ideally return month-scoped + aggregates
        ]);

        if (!lawyersRes.ok || !firmRes.ok || !statsRes.ok) {
          throw new Error("One or more requests failed");
        }

        const lawyersJson = await lawyersRes.json();
        const firmJson = await firmRes.json();
        const statsJson = await statsRes.json();

        const firmData = {
          monthly_budget: firmJson?.firm?.monthly_budget ?? null,
          currency: firmJson?.firm?.currency ?? "USD",
          timezone: firmJson?.firm?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
          price_per_appt: firmJson?.firm?.price_per_appt ?? PRICE_PER_APPT_FALLBACK,
        };

        setLawyers(lawyersJson?.lawyers ?? []);
        setFirm(firmData);
        setAppointments(statsJson?.appointments ?? []);
        setEditBudget(firmData.monthly_budget?.toString() ?? "");
      } catch (e) {
        setError(e.message || "Failed to load budget data");
        setLawyers([]);
        setFirm(null);
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [firmId]);

  // Current month range (consider firm.timezone if you adopt a TZ lib)
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

  // Derived values
  const {
    enabledLawyersCount,
    completedApptsThisMonth,
    confirmedApptsThisMonth,
    totalApptsThisMonth,
    totalSpent,
    remainingBudget,
    newClientsBookable,
    monthProgressRatio,
    softAlert,
    hardAlert
  } = useMemo(() => {
    const enabled = lawyers.filter(l => l.status === "Active").length;

    // Helper function to check if client has booked with this lawyer before
    const isFirstTimeWithLawyer = (clientId, lawyerId, appointmentTime) => {
      // Check if this client has any previous appointments with this lawyer
      const previousAppointments = appointments.filter(a => 
        a.client_id === clientId && 
        a.lawyer_id === lawyerId && 
        new Date(a.appointment_time) < new Date(appointmentTime)
      );
      return previousAppointments.length === 0;
    };

    const monthCompleted = appointments.filter(a => {
      if (a.status !== "completed") return false;
      const t = new Date(a.appointment_time);
      return t >= startOfMonth && t <= endOfMonth;
    });

    // Also include confirmed appointments (accepted but not yet completed)
    const monthConfirmed = appointments.filter(a => {
      if (a.status !== "confirmed") return false;
      const t = new Date(a.appointment_time);
      return t >= startOfMonth && t <= endOfMonth;
    });

    // Filter for first-time clients only (same logic as billing system)
    const firstTimeCompleted = monthCompleted.filter(a => 
      isFirstTimeWithLawyer(a.client_id, a.lawyer_id, a.appointment_time)
    );
    
    const firstTimeConfirmed = monthConfirmed.filter(a => 
      isFirstTimeWithLawyer(a.client_id, a.lawyer_id, a.appointment_time)
    );

    // Combine first-time completed and confirmed appointments for budget calculation
    const monthAppointments = [...firstTimeCompleted, ...firstTimeConfirmed];

    // Prefer summed price from data; fall back to firm.price_per_appt
    const unit =  PRICE_PER_APPT_FALLBACK;
    const spent = monthAppointments.reduce((sum, a) => {
      // Use the platform fee ($110) instead of the actual appointment price
      return sum + unit;
    }, 0);

    const budget = firm?.monthly_budget; // Can be null (unlimited) or number >= 0
    const remaining = budget !== null ? Math.max(0, budget - spent) : null;
    const bookable = remaining !== null ? Math.floor(remaining / unit) : null;

    // Pacing vs month progress
    const daysInMonth = endOfMonth.getDate();
    const dayOfMonth = now.getDate();
    const monthProgress = Math.min(1, dayOfMonth / daysInMonth);
    const soft = budget !== null ? spent >= budget * SOFT_ALERT_RATIO : false;
    const hard = budget !== null ? spent >= budget : false;

    return {
      enabledLawyersCount: enabled,
      completedApptsThisMonth: firstTimeCompleted.length,
      confirmedApptsThisMonth: firstTimeConfirmed.length,
      totalApptsThisMonth: monthAppointments.length,
      totalSpent: spent,
      remainingBudget: remaining,
      newClientsBookable: bookable,
      monthProgressRatio: monthProgress,
      softAlert: soft,
      hardAlert: hard
    };
  }, [appointments, lawyers, firm, startOfMonth, endOfMonth]);

  async function handleSaveBudget() {
    if (!firmId) return;
    const value = editBudget === "" ? null : Number(editBudget);
    if (editBudget !== "" && (Number.isNaN(value) || value < 0)) {
      alert("Enter a valid non-negative number or leave empty for unlimited.");
      return;
    }
    try {
      setSavingBudget(true);
      const res = await authenticatedFetch(`/api/attorney/webapp/updateFirmBudget`, {
        method: "PATCH",
        body: JSON.stringify({ firm_id: firmId, monthly_budget: value })
      });
      if (!res.ok) throw new Error("Failed to save budget");
      setFirm(prev => prev ? { ...prev, monthly_budget: value } : prev);
    } catch (e) {
      alert(e.message || "Failed to save budget");
    } finally {
      setSavingBudget(false);
    }
  }

  // Simple progress values
  const budgetVal = firm?.monthly_budget;
  const spentPct = budgetVal !== null ? Math.min(100, Math.round((totalSpent / budgetVal) * 100)) : 0;
  const pacePct = Math.round(monthProgressRatio * 100);

  return (
    <div className="budget-management-section">
      <div className="bookings-card" style={{ padding: 24 }}>
        <div className="bookings-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h4 className="bookings-card-title">Budget Overview</h4>
          {firm && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="number"
                min="0"
                placeholder="Monthly budget (leave empty for unlimited)"
                value={editBudget}
                onChange={e => setEditBudget(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ccc", width: 200 }}
              />
              <button onClick={handleSaveBudget} disabled={savingBudget} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #144a8c" }}>
                {savingBudget ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 24 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, color: "#b00020" }}>Error: {error}</div>
        ) : (
          <div className="bookings-content" style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <div className="bookings-content-left" style={{ flex: "1 1 0%" }}>
              <div style={{ marginBottom: 12 }}>
                <p style={{ margin: "4px 0", fontSize: 16, fontWeight: 600 }}>
                  Monthly Budget:{" "}
                  <span style={{ color: "#144a8c" }}>
                    {budgetVal !== null ? `$${budgetVal.toLocaleString()}` : "Unlimited"}
                  </span>
                </p>
                <p style={{ margin: "4px 0", fontSize: 16, fontWeight: 600 }}>
                  Spent This Month: <span style={{ color: hardAlert ? "#b00020" : "#ff6b35" }}>${totalSpent.toLocaleString()}</span>
                </p>
                <p style={{ margin: "4px 0", fontSize: 16, fontWeight: 600 }}>
                  Remaining:{" "}
                  <span style={{ color: "#28a745" }}>
                    {remainingBudget !== null ? `$${remainingBudget.toLocaleString()}` : "Unlimited"}
                  </span>
                </p>
                <div style={{ marginTop: 12 }}>
                  {/* simple progress vs budget and vs month time */}
                  {budgetVal !== null ? (
                    <>
                      <div style={{ fontSize: 12, marginBottom: 4 }}>Budget used: {spentPct}%</div>
                      <div style={{ height: 8, background: "#eee", borderRadius: 6 }}>
                        <div style={{ width: `${spentPct}%`, height: 8, borderRadius: 6, background: hardAlert ? "#b00020" : softAlert ? "#ff6b35" : "#144a8c" }} />
                      </div>
                    </>
                  ) : null}
                  <div style={{ fontSize: 12, marginTop: 8 }}>Month elapsed: {pacePct}%</div>
                </div>
                {softAlert && !hardAlert && <div style={{ marginTop: 8, fontSize: 13, color: "#ff6b35" }}>Heads up: you've crossed 80% of this month's budget.</div>}
                {hardAlert && <div style={{ marginTop: 8, fontSize: 13, color: "#b00020" }}>Budget exceeded. Consider pausing sponsored listings or raising the cap.</div>}
              </div>
              
              {/* Donut Chart */}
              <div style={{ marginTop: 30, display: 'flex', justifyContent: 'center' }}>
                <BudgetDonutChart 
                  spent={totalSpent} 
                  budget={budgetVal} 
                  size={140}
                  confirmedSpent={confirmedApptsThisMonth * PRICE_PER_APPT_FALLBACK}
                />
              </div>
            </div>

            <div style={{ 
              width: 1, 
              backgroundColor: "#D9D9D9",
              alignSelf: "stretch",
              margin: "0 24px"
            }} />

            <div className="bookings-content-right" style={{ flex: 1 }}>
              <p className="bookings-card-subtitle" style={{ marginBottom: 8 }}>New Clients Bookable</p>
              <h3 style={{ margin: "8px 0", fontSize: 28, fontWeight: 700, color: "#144a8c" }}>
                {newClientsBookable !== null ? newClientsBookable : "∞"}
              </h3>
              <p style={{ fontSize: 14, color: "#666", margin: "8px 0 0 0" }}>
                {enabledLawyersCount} lawyers enabled for search • {completedApptsThisMonth} first-time completed, {confirmedApptsThisMonth} first-time confirmed this month
              </p>
              {/* Per-lawyer mini breakdown (top 3) */}
              <div style={{ marginTop: 12 }}>
                <PerLawyerMiniTable appointments={appointments} lawyers={lawyers} startOfMonth={startOfMonth} endOfMonth={endOfMonth} unitPrice={firm?.price_per_appt ?? PRICE_PER_APPT_FALLBACK} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PerLawyerMiniTable({ appointments, lawyers, startOfMonth, endOfMonth, unitPrice }) {
  const byId = useMemo(() => Object.fromEntries(lawyers.map(l => [l.id ?? l.lawyer_id, l])), [lawyers]);
  const rows = useMemo(() => {
    const map = new Map();
    for (const a of appointments) {
      if (a.status !== "completed") continue;
      const t = new Date(a.appointment_time);
      if (t < startOfMonth || t > endOfMonth) continue;
      const id = a.lawyer_id;
      const price = typeof a.price === "number" ? a.price : unitPrice;
      // Use the lawyer data from the appointment if available, otherwise fall back to lawyers array
      const lawyer = a.lawyer || byId[id];
      const prev = map.get(id) || { 
        lawyer_id: id, 
        name: lawyer?.name || "Unknown", 
        first_name: lawyer?.first_name || "Unknown",
        last_name: lawyer?.last_name || "",
        profile_photo_url: lawyer?.profile_photo_url || null,
        completed: 0, 
        spent: 0 
      };
      prev.completed += 1;
      prev.spent += price;
      map.set(id, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.spent - a.spent).slice(0, 3);
  }, [appointments, startOfMonth, endOfMonth, unitPrice, byId]);

  if (!rows.length) return null;
  return (
    <div style={{ fontSize: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Top lawyers by spend</div>
      {rows.map(r => (
        <div key={r.lawyer_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {r.profile_photo_url ? (
              <img 
                src={r.profile_photo_url} 
                alt={`${r.first_name} ${r.last_name}`}
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #e0e0e0"
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div 
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  color: "#666"
                }}
              >
                {r.first_name?.[0]}{r.last_name?.[0]}
              </div>
            )}
            <span>{r.first_name} {r.last_name}</span>
          </div>
          <span>{r.completed} booking(s) • ${r.spent.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// Donut Chart Component for Budget Visualization
function BudgetDonutChart({ spent, budget, size = 120, confirmedSpent = 0 }) {
  if (budget === null) {
    // For unlimited budgets, show a different visualization
    return (
      <div style={{ 
        width: size, 
        height: size, 
        borderRadius: '50%', 
        background: 'conic-gradient(#144a8c 0deg 360deg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        margin: '20px 0'
      }}>
        <div style={{
          width: size * 0.6,
          height: size * 0.6,
          borderRadius: '50%',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 600,
          color: '#144a8c',
          textAlign: 'center'
        }}>
          Unlimited
        </div>
      </div>
    );
  }

  // Handle edge cases
  if (budget === 0) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        borderRadius: '50%', 
        background: '#e3eefc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        margin: '20px 0'
      }}>
        <div style={{
          width: size * 0.6,
          height: size * 0.6,
          borderRadius: '50%',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 600,
          color: '#666',
          textAlign: 'center'
        }}>
          No Budget
        </div>
      </div>
    );
  }

  const completedSpent = spent - confirmedSpent;
  const totalSpent = spent;
  const percentage = Math.min(100, (totalSpent / budget) * 100);
  const remaining = 100 - percentage;
  
  // Use primary color for spent, contrasting color for remaining
  const spentColor = percentage >= 100 ? '#b00020' : '#144a8c'; // Red if exceeded, blue otherwise
  const remainingColor = '#e3eefc'; // Light blue for contrast
  
  return (
    <div style={{ 
      width: size, 
      height: size, 
      borderRadius: '50%', 
      background: `conic-gradient(${spentColor} 0deg ${percentage * 3.6}deg, ${remainingColor} ${percentage * 3.6}deg 360deg)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      margin: '20px 0'
    }}>
      <div style={{
        width: size * 0.6,
        height: size * 0.6,
        borderRadius: '50%',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 700,
        color: spentColor
      }}>
        <span>{percentage.toFixed(0)}%</span>
        <span style={{ fontSize: '10px', color: '#666' }}>used</span>
      </div>
      
      {/* Legend */}
      <div style={{
        position: 'absolute',
        right: -160,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '11px',
        color: '#555',
        lineHeight: '1.4'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: spentColor,
            marginRight: '6px',
            flexShrink: 0
          }}></div>
          <span>Total: ${totalSpent.toLocaleString()}</span>
        </div>
        {confirmedSpent > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: '#ff6b35',
              marginRight: '6px',
              flexShrink: 0
            }}></div>
            <span>Pending: ${confirmedSpent.toLocaleString()}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: remainingColor,
            marginRight: '6px',
            flexShrink: 0
          }}></div>
          <span>Remaining: ${Math.max(0, budget - totalSpent).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

// Sub-modal: Set/Update Budget
function BudgetModal({ open, onClose, firmId, setSnackbar }) {
  const [loading, setLoading] = useState(false);
  const [budget, setBudget] = useState('');
  const [currentBudget, setCurrentBudget] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
          if (open && firmId) {
        setLoading(true);
        authenticatedFetch(`/api/attorney/webapp/getFirmBudget?firm_id=${firmId}`)
          .then(res => res.json())
          .then(data => {
            setCurrentBudget(data.firm?.monthly_budget || null);
            setBudget(data.firm?.monthly_budget?.toString() || '');
          })
          .catch(() => setCurrentBudget(null))
          .finally(() => setLoading(false));
      }
  }, [open, firmId]);

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authenticatedFetch('/api/attorney/webapp/setFirmBudget', {
        method: 'POST',
        body: JSON.stringify({ firm_id: firmId, monthly_budget: parseFloat(budget) }),
      });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Budget updated!', severity: 'success' });
        onClose();
      } else {
        setError(data.error || 'Failed to update budget');
      }
    } catch (e) {
      setError('Failed to update budget');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '20px', fontFamily: 'Poppins', minWidth: 400, maxWidth: 700 } }}>
      <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 700, color: '#144a8c' }}>Set Monthly Spend Cap</DialogTitle>
      <DialogContent sx={{ fontFamily: 'Poppins' }}>
        <Typography sx={{ mb: 2, color: '#555' }}>
          Enter the maximum amount your firm can spend per month. Once reached, new bookings will be blocked.
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <label style={{ fontWeight: 600, color: '#144a8c' }}>Monthly Budget (USD)</label>
          <input
            type="number"
            min={0}
            value={budget}
            onChange={e => setBudget(e.target.value)}
            style={{
              borderRadius: 12,
              border: '1px solid #cfd8dc',
              padding: '10px 14px',
              fontFamily: 'Poppins',
              fontSize: 18,
              outline: 'none',
              width: '100%',
            }}
            disabled={loading}
          />
          {currentBudget !== null && (
            <Typography sx={{ color: '#888', fontSize: 14 }}>
              Current budget: <b>${currentBudget}</b>
            </Typography>
          )}
          {error && <Typography sx={{ color: 'red', fontSize: 14 }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontFamily: 'Poppins' }}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={{ background: '#144a8c', fontFamily: 'Poppins', borderRadius: 2 }} disabled={loading || !budget}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Sub-modal: Manage Lawyer Search Visibility
function LawyersModal({ open, onClose, firmId, setSnackbar }) {
  const [loading, setLoading] = useState(false);
  const [lawyers, setLawyers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && firmId) {
      setLoading(true);
      authenticatedFetch(`/api/attorney/fetchFirmMembers?firm_id=${firmId}`)
        .then(res => res.json())
        .then(data => {
          setLawyers(data?.lawyers || []);
          setSelected((data?.lawyers || []).filter(l => l.status === 'active').map(l => l.user_id));
        })
        .catch(() => setLawyers([]))
        .finally(() => setLoading(false));
    }
  }, [open, firmId]);

  const handleToggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authenticatedFetch('/api/attorney/webapp/setLawyerSearchVisibility', {
        method: 'POST',
        body: JSON.stringify({ firm_id: firmId, lawyer_ids: selected }),
      });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Lawyer visibility updated!', severity: 'success' });
        onClose();
      } else {
        setError(data.error || 'Failed to update visibility');
      }
    } catch (e) {
      setError('Failed to update visibility');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '20px', fontFamily: 'Poppins', minWidth: 400, maxWidth: 700 } }}>
      <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 700, color: '#144a8c' }}>Manage Lawyer Search Visibility</DialogTitle>
      <DialogContent sx={{ fontFamily: 'Poppins' }}>
        <Typography sx={{ mb: 2, color: '#555' }}>
          Select which lawyers are visible in search and open to new client bookings.
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          {lawyers.map(lawyer => (
            <Box key={lawyer.user_id} display="flex" alignItems="center" gap={1} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1, mb: 1, background: selected.includes(lawyer.user_id) ? '#e3eefc' : '#fff' }}>
              <input
                type="checkbox"
                checked={selected.includes(lawyer.user_id)}
                onChange={() => handleToggle(lawyer.user_id)}
                disabled={loading}
                style={{ accentColor: '#144a8c', width: 18, height: 18 }}
              />
              <Typography sx={{ fontFamily: 'Poppins', fontWeight: 500, color: '#144a8c', fontSize: 16 }}>{lawyer.first_name} {lawyer.last_name}</Typography>
              <Typography sx={{ color: '#888', fontSize: 14, ml: 1 }}>{lawyer.email}</Typography>
            </Box>
          ))}
          {error && <Typography sx={{ color: 'red', fontSize: 14 }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontFamily: 'Poppins' }}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={{ background: '#144a8c', fontFamily: 'Poppins', borderRadius: 2 }} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Sub-modal: Pause/Unpause Spending
function PauseModal({ open, onClose, firmId, setSnackbar }) {
  const [loading, setLoading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && firmId) {
      setLoading(true);
      authenticatedFetch(`/api/attorney/webapp/getFirmBudget?firm_id=${firmId}`)
        .then(res => res.json())
        .then(data => {
          setPaused(data.firm?.listings_paused || false);
        })
        .catch(() => setPaused(false))
        .finally(() => setLoading(false));
    }
  }, [open, firmId]);

  const handlePause = async (pause) => {
    setLoading(true);
    setError('');
    try {
      const res = await authenticatedFetch('/api/attorney/webapp/pauseFirmSpending', {
        method: 'POST',
        body: JSON.stringify({ firm_id: firmId, pause }),
      });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: pause ? 'Spending paused!' : 'Spending unpaused!', severity: 'success' });
        onClose();
      } else {
        setError(data.error || 'Failed to update status');
      }
    } catch (e) {
      setError('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '20px', fontFamily: 'Poppins', minWidth: 400, maxWidth: 700 } }}>
      <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 700, color: '#144a8c' }}>Pause/Unpause Firm Spending</DialogTitle>
      <DialogContent sx={{ fontFamily: 'Poppins' }}>
        <Typography sx={{ mb: 2, color: '#555' }}>
          {paused
            ? 'Spending is currently paused. New clients cannot book with your firm.'
            : 'You can pause all new bookings for your firm. This can be undone at any time.'}
        </Typography>
        {error && <Typography sx={{ color: 'red', fontSize: 14 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontFamily: 'Poppins' }}>Cancel</Button>
        {paused ? (
          <Button onClick={() => handlePause(false)} variant="contained" sx={{ background: '#144a8c', fontFamily: 'Poppins', borderRadius: 2 }} disabled={loading}>
            {loading ? 'Unpausing...' : 'Unpause Spending'}
          </Button>
        ) : (
          <Button onClick={() => handlePause(true)} variant="contained" sx={{ background: '#144a8c', fontFamily: 'Poppins', borderRadius: 2 }} disabled={loading}>
            {loading ? 'Pausing...' : 'Pause Spending'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default Dashboard;
